import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { FetchPolicy, MutationFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import {
	ClientSponsor,
	GetLocalizationRequest,
	LocalizationResponse,
	LocType,
	PortalTypeEnum,
} from '@ats/api/cm';
import {
	CmCulture,
	CmLocalizationService,
	CmSettingsService,
	PortalCoreFeature,
	PublicLocalization,
} from '@ats/cm/core';
import { AtsNotificationService } from '@ats/notifications';
import { MatTabChangeEvent } from '@ats/ui';
import { LOC_INJECTOR, LocalizationProvider } from '@cm/core/localization';
import { TINYMCE } from '@cm/shared/ui/tinymce';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { map, pluck, switchMap, take } from 'rxjs/operators';
import {
	cpLocalizeModalLocs,
	LocalizeModalLocType,
	spLocalizeModalLocs,
} from './localize-modal.locs';

const INDIVIDUAL_LOCALIZATION = gql`
	query individualLocalization($request: GetLocalizationRequest!) {
		clientSponsor {
			localization(request: $request) {
				name
				cultureName
				defaultValue
				description
				htmlAllowed
				path
				portalType
				shared
				type
				uiType
				value
				isCustomized
			}
		}
	}
`;

const UPDATE_LOCALIZATION = gql`
	mutation updateLocalizationList($request: UpdateLocalizationListRequest!) {
		updateLocalizationList(request: $request) {
			name
			type
			value
			htmlAllowed
			isCustomized
		}
	}
`;

const DELETE_LOCALIZATION = gql`
	mutation deleteLocalizationList($request: DeleteLocalizationListRequest!) {
		deleteLocalizationList(request: $request) {
			name
			type
			value
			htmlAllowed
			isCustomized
		}
	}
`;

const CREATE_LOCALIZATION = gql`
	mutation createLocalizationList($request: CreateLocalizationListRequest!) {
		createLocalizationList(request: $request) {
			name
			type
			value
			htmlAllowed
			isCustomized
		}
	}
`;

interface ReturnedLocalization {
	type: LocType;
	name: string;
	value: string;
	cultureName: string;
}

/**
 * @deprecated Use localize-dialog from @cm/ui/localize
 */
@UntilDestroy()
@Component({
	selector: 'cm-localize-modal',
	templateUrl: './localize-modal.component.html',
	styleUrls: ['./localize-modal.component.scss'],
	providers: [LocalizationProvider({ ...spLocalizeModalLocs, ...cpLocalizeModalLocs })],
	standalone: false,
})
export class CmLocalizeModalComponent implements AfterViewInit, OnInit {
	cultures$: Observable<CmCulture[]>;
	supportedCultures$: Observable<CmCulture[]>;
	newVal: string = '';
	activeTabIndex: number = 0;
	languageForm: UntypedFormGroup;
	displayVal: string;
	localization!: LocalizationResponse;
	readonly tiny = TINYMCE;
	readonly locFeature: PortalCoreFeature;
	private hasCodeChanges: boolean = false;
	isCP: boolean = false;

	get sanitizedValue() {
		return this.sanitizer.bypassSecurityTrustHtml(this.newVal);
	}

	constructor(
		public dialogRef: MatDialogRef<CmLocalizeModalComponent>,
		@Inject(MAT_DIALOG_DATA) private data: PublicLocalization,
		@Inject(LOC_INJECTOR) private locs: LocalizeModalLocType,
		private sanitizer: DomSanitizer,
		private apollo: Apollo,
		private localizationService: CmLocalizationService,
		private settings: CmSettingsService,
		private fb: UntypedFormBuilder,
		private notificationService: AtsNotificationService
	) {
		this.locFeature = this.settings.portalCoreFeature;
		this.isCP = this.settings.portal === PortalTypeEnum.CandidatePortal;
	}

	ngOnInit() {
		this.cultures$ = this.settings.clientCultures$;
		this.supportedCultures$ = this.settings.supportedCultures$;
	}

	ngAfterViewInit() {
		// Hack for TinyMCE issue with MatTabs
		// https://github.com/tinymce/tinymce-angular/issues/76
		setTimeout(() => this.initialize(), 0);
	}

	get language(): UntypedFormControl {
		return this.languageForm.get('language') as UntypedFormControl;
	}

	handleTabChange(event: MatTabChangeEvent): void {
		this.activeTabIndex = event.index;
	}

	deleteLoc(): void {
		this.saveLocalization(true);
	}

	saveLocalization(deleteLocalization = false): void {
		this.settings.culture$.pipe(take(1)).subscribe(culture => {
			const fetchPolicy = culture.code === this.language.value ? 'network-only' : 'no-cache';
			if ((this.newVal || this.newVal === '') && !deleteLocalization) {
				if (this.localization.isCustomized) {
					this.updateLocalization(this.newVal, fetchPolicy);
				} else {
					this.createLocalization(this.newVal, fetchPolicy);
				}
			} else {
				this.deleteLocalization(fetchPolicy);
			}
		});
	}

	closeDialog(codeModified?: boolean): void {
		if (codeModified) {
			this.notificationService.popInfo({
				id: 'localize-modal-close',
				message: this.localizationService.localize(
					`${this.locFeature}/System.LocalizeModal.NotificationMessage.CodeModified`,
					'Your localization code may have been modified for security reasons. Please verify the result.'
				),
			});
		}
		this.dialogRef.close();
	}

	private initialize(): void {
		this.settings.culture$
			.pipe(
				take(1),
				switchMap(culture =>
					this.fetchIndividualLocalization$({
						cultureName: culture.code,
						name: this.data.name,
						type: this.data.type,
					})
				)
			)
			.subscribe(res => {
				this.languageForm = this.createLanguageForm(res.cultureName);
				this.displayVal = res.value;
				this.newVal = res.value;
				this.localization = res;
				this.listenToLanguageChanges$();
			});
	}

	private listenToLanguageChanges$(): void {
		this.language.valueChanges
			.pipe(
				untilDestroyed(this),
				switchMap(lang => {
					if (this.localization.cultureName === lang) {
						return of(null);
					} else {
						return this.fetchIndividualLocalization$({
							cultureName: lang,
							name: this.localization.name,
							type: this.localization.type,
						});
					}
				})
			)
			.subscribe(localizationNewLang => {
				if (localizationNewLang) {
					this.localization = localizationNewLang;
				}
				if (this.localization.isCustomized) {
					this.displayVal = this.localization.value;
					this.newVal = this.localization.value;
				} else {
					this.newVal = null;
					this.displayVal = null;
				}
			});
	}

	private createLanguageForm(langCode: string): UntypedFormGroup {
		return this.fb.group({
			language: [langCode],
		});
	}

	private fetchIndividualLocalization$(
		request: GetLocalizationRequest,
		fetchPolicy: FetchPolicy = 'network-only'
	): Observable<LocalizationResponse> {
		return this.apollo
			.watchQuery<{ clientSponsor: ClientSponsor }, { request: GetLocalizationRequest }>({
				query: INDIVIDUAL_LOCALIZATION,
				variables: { request },
				fetchPolicy,
			})
			.valueChanges.pipe(take(1), pluck('data', 'clientSponsor', 'localization'));
	}

	private updateLocalization(newVal: string = '', fetchPolicy: MutationFetchPolicy): void {
		this.apollo
			.mutate<{ updateLocalizationList: ReturnedLocalization[] }>({
				mutation: UPDATE_LOCALIZATION,
				fetchPolicy,
				variables: {
					request: {
						cultureName: this.language.value,
						type: this.localization.type,
						localizationList: [
							{
								name: this.localization.name,
								value: newVal,
							},
						],
					},
				},
			})
			.pipe(map(({ data }) => (data.updateLocalizationList[0] || {}).value))
			.subscribe(loc => {
				this.notificationService.showSuccess(
					this.locs[
						`${
							this.isCP
								? 'CPSystemLocalizeModalChangesSaved'
								: 'SPSystemLocalizeModalChangesSaved'
						}`
					]
				);
				this.closeDialog(this.hasCodeChanges);
			});
	}

	private deleteLocalization(fetchPolicy: MutationFetchPolicy): void {
		this.apollo
			.mutate<{ deleteLocalizationList: ReturnedLocalization[] }>({
				mutation: DELETE_LOCALIZATION,
				fetchPolicy,
				variables: {
					request: {
						cultureName: this.language.value,
						type: this.localization.type,
						localizationList: [
							{
								name: this.localization.name,
							},
						],
					},
				},
			})
			.subscribe(() => {
				this.closeDialog();
			});
	}

	private createLocalization(newVal: string = '', fetchPolicy: MutationFetchPolicy): void {
		this.apollo
			.mutate<{ createLocalizationList: ReturnedLocalization[] }>({
				mutation: CREATE_LOCALIZATION,
				fetchPolicy,
				variables: {
					request: {
						cultureName: this.language.value,
						type: this.localization.type,
						localizationList: [
							{
								name: this.localization.name,
								value: newVal,
							},
						],
					},
				},
			})
			.pipe(map(({ data }) => (data.createLocalizationList[0] || {}).value))
			.subscribe(loc => {
				this.closeDialog(this.hasCodeChanges);
			});
	}
}
